import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { MODE_LIST } from './modes';
import APIService from '../../services/APIService';
import * as sessionStore from './sessionStore';
import {
  IconHistory, IconSearch, IconFilter, IconCheck, IconX,
  IconChevRight, IconChevDown, IconSpark, IconClock, IconTarget, IconArrowRight,
} from './icons';
import { LearningScene } from './learningAssets';

const KIND_LABEL = { topic: 'Topic', subtopic: 'Sub-topic', concept: 'Concept', node: 'Node' };
const ACCENT = 'var(--n-primary)';

// All leaf ids beneath a node (or the node itself if it's a leaf).
const leavesOf = (node) =>
  (node.children && node.children.length) ? node.children.flatMap(leavesOf) : [node.id];

// --- session options (the config surface the planner reasons over) -----------
const DEFAULT_OPTIONS = {
  focus: 'balanced',          // review | new | balanced
  difficulty: null,           // theory/mcq/flashcards — null = adaptive
  codingDifficulty: null,     // DSA (real filter) — SEPARATE; null = adaptive
  sessionSize: 5,             // questions per mode
  weakPriority: 'medium',     // low | medium | high
};
const OPTION_ROWS = [
  { key: 'focus', label: 'Focus', choices: [['review', 'Review'], ['balanced', 'Balanced'], ['new', 'New']] },
  { key: 'difficulty', label: 'Difficulty', choices: [[null, 'Adaptive'], ['easy', 'Easy'], ['medium', 'Medium'], ['hard', 'Hard']] },
  { key: 'codingDifficulty', label: 'Coding difficulty', choices: [[null, 'Adaptive'], ['Easy', 'Easy'], ['Medium', 'Medium'], ['Hard', 'Hard']] },
  { key: 'sessionSize', label: 'Session size', choices: [[3, 'Quick · 3'], [5, 'Standard · 5'], [10, 'Deep · 10']] },
  { key: 'weakPriority', label: 'Weak-spot priority', choices: [['low', 'Low'], ['medium', 'Medium'], ['high', 'High']] },
];

// Staged captions + a live mm:ss timer while the planning agent thinks.
const PLANNER_STAGES = [
  'Reading your memory…',
  'Ranking your concepts…',
  'Weighing weak spots against new ground…',
  'Composing your plan…',
];
const PlannerLoader = () => {
  const [t, setT] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setT((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const mm = String(Math.floor(t / 60)).padStart(2, '0');
  const ss = String(t % 60).padStart(2, '0');
  const stage = PLANNER_STAGES[Math.min(Math.floor(t / 2), PLANNER_STAGES.length - 1)];
  return (
    <div className="nano-planner-veil" role="status" aria-live="polite">
      <div className="nano-planner-card">
        <div className="nano-planner-orb"><span /><span /><span /></div>
        <div className="nano-planner-title">Your agent is planning this session</div>
        <div className="nano-planner-stage" key={stage}>{stage}</div>
        <div className="nano-planner-bar"><span /></div>
        <div className="nano-planner-timer">{mm}:{ss}</div>
      </div>
    </div>
  );
};

const fmtWhen = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)} h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const OPT_LABEL = {
  focus: (v) => v && `Focus: ${v}`,
  difficulty: (v) => `Difficulty: ${v || 'adaptive'}`,
  codingDifficulty: (v) => `Coding: ${v || 'adaptive'}`,
  sessionSize: (v) => v && `${v} per mode`,
  weakPriority: (v) => v && `Weak-spot: ${v}`,
};

const SessionSettings = ({ onPlanReady, savedSession, onResume, onViewPlan, onNewSession }) => {
  const [tree, setTree] = useState([]);
  const [loadingTree, setLoadingTree] = useState(true);
  const [treeErr, setTreeErr] = useState('');
  // Selection + options restore from the persisted draft, so switching tabs
  // (which unmounts this screen) never loses what the learner picked.
  const [selected, setSelected] = useState(() => new Set(sessionStore.loadDraft()?.sel || []));
  const [expanded, setExpanded] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const [starting, setStarting] = useState(false);
  const [startErr, setStartErr] = useState('');
  const [opts, setOpts] = useState(() => ({ ...DEFAULT_OPTIONS, ...(sessionStore.loadDraft()?.opts || {}) }));

  useEffect(() => {
    sessionStore.saveDraft({ sel: Array.from(selected), opts });
  }, [selected, opts]);

  useEffect(() => {
    let alive = true;
    APIService.getHubTree()
      .then((res) => {
        if (!alive) return;
        const t = res.tree || [];
        setTree(t);
        setExpanded(new Set(t.map((n) => n.id)));
      })
      .catch((e) => alive && setTreeErr(e.message || 'Could not load the Knowledge Hub.'))
      .finally(() => alive && setLoadingTree(false));
    return () => { alive = false; };
  }, []);

  // Tri-state for a node, derived from how many of its leaves are selected.
  const stateOf = useCallback((node) => {
    const leaves = leavesOf(node);
    let on = 0;
    for (const id of leaves) if (selected.has(id)) on += 1;
    return on === 0 ? 'off' : on === leaves.length ? 'on' : 'ind';
  }, [selected]);

  // Toggle a node → cascade to every leaf beneath it.
  const toggle = (node) => {
    const leaves = leavesOf(node);
    const allOn = leaves.every((id) => selected.has(id));
    setSelected((prev) => {
      const n = new Set(prev);
      leaves.forEach((id) => (allOn ? n.delete(id) : n.add(id)));
      return n;
    });
  };
  const toggleExpand = (id) =>
    setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // Minimal "cover" of the selection — top-most fully-selected nodes + partial leaves.
  const cover = useMemo(() => {
    const out = [];
    const walk = (node) => {
      const st = stateOf(node);
      if (st === 'off') return;
      if (st === 'on') { out.push(node); return; }
      if (node.children && node.children.length) node.children.forEach(walk);
      else out.push(node);
    };
    tree.forEach(walk);
    return out;
  }, [tree, stateOf]);

  const q = search.trim().toLowerCase();
  const hasMatch = (node) => !q || node.name.toLowerCase().includes(q) || (node.children || []).some(hasMatch);

  const renderNode = (node, depth) => {
    if (!hasMatch(node)) return null;
    const hasChildren = node.children && node.children.length > 0;
    const isOpen = expanded.has(node.id) || (!!q && hasChildren);
    const st = stateOf(node);
    const cls = depth === 0 ? '' : depth === 1 ? 'child' : 'gchild';
    return (
      <div key={node.id}>
        <div className={`nano-tree-node ${cls}${st !== 'off' ? ' on' : ''}`}>
          {hasChildren ? (
            <span className="nano-caret" onClick={() => toggleExpand(node.id)}>
              {isOpen ? <IconChevDown size={13} /> : <IconChevRight size={13} />}
            </span>
          ) : (
            <span className="nano-caret" />
          )}
          <span
            className={`nano-chk${st === 'on' ? ' on' : ''}`}
            onClick={() => toggle(node)}
            style={st === 'ind' ? { background: ACCENT, borderColor: ACCENT } : undefined}
            title={st === 'on' ? 'Selected' : st === 'ind' ? 'Some sub-topics selected' : 'Not selected'}
          >
            {st === 'on' ? <IconCheck size={11} /> : st === 'ind' ? <span style={{ width: 8, height: 2, background: '#0b0d14', borderRadius: 1, display: 'block' }} /> : null}
          </span>
          <span onClick={() => toggle(node)} style={{ flex: 1 }}>
            {node.name}
            {node.item_count ? <em style={{ color: 'var(--n-text-3)', fontStyle: 'normal' }}> · {node.item_count}</em> : null}
          </span>
          {hasChildren && (
            <span style={{ fontSize: 11, color: 'var(--n-text-3)', fontFamily: 'ui-monospace, Menlo, monospace' }}>{leavesOf(node).length}</span>
          )}
        </div>
        {hasChildren && isOpen && node.children.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  const estMin = 15 + cover.length * 6;

  // "Build my plan" — the agent reasons over memory + tags and PROPOSES a plan
  // (fast; content materializes in the background during plan review).
  const start = async () => {
    if (starting || cover.length === 0) return;
    setStarting(true);
    setStartErr('');
    const subjects = cover.map((n) => ({ id: n.id, name: n.name, kind: n.kind }));
    const conceptIds = subjects.map((s) => s.id);
    const includedModes = MODE_LIST.map((m) => m.id);   // all modes available; pick from the left pane
    try {
      const plan = await APIService.buildPlan({
        conceptIds, modes: includedModes,
        focus: opts.focus, difficulty: opts.difficulty,
        codingDifficulty: opts.codingDifficulty,
        weakPriority: opts.weakPriority, countPerMode: opts.sessionSize,
      });
      onPlanReady({ subjects, includedModes, options: opts, plan });
    } catch (e) {
      setStartErr(e.message || 'Could not build a plan. Is the backend running?');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="nano-settings">
      <div className="nano-set-head nano-set-head--story">
        <div className="nano-set-copy">
          <div className="nano-set-kicker"><LearningScene name="theory" size={16} /> AI-guided session</div>
          <div className="nano-set-title">Build a useful next rep.</div>
          <div className="nano-set-sub">Choose the material; your agent will use your memory to propose the right mix of review, stretch, and practice.</div>
        </div>
        <div className="nano-set-art" aria-hidden="true"><LearningScene name="theory" size={70} /></div>
        {savedSession && (
          <button className="nano-ghost-btn" type="button" onClick={onResume}>
            <IconHistory size={15} /> {savedSession.status === 'planned' ? 'Review Your Plan' : 'Resume Previous Session'}
          </button>
        )}
      </div>

      {savedSession && (
        <div className="nano-cursess">
          <div className="nano-cursess-main">
            <div className="nano-cursess-title">
              Current session{' '}
              <span className="nano-cursess-badge" style={savedSession.status === 'planned' ? { color: 'var(--n-violet-ink)', background: 'var(--n-violet-soft)', borderColor: 'var(--n-primary-line)' } : undefined}>
                {savedSession.status === 'planned' ? 'Planned — not started' : 'Active'}
              </span>
            </div>
            <div className="nano-cursess-sub">
              Started {fmtWhen(savedSession.startedAt)} · updated {fmtWhen(savedSession.updatedAt)}
              {savedSession.status !== 'planned' && savedSession.mode ? <> · last in <b>{savedSession.mode}</b></> : null}
            </div>
            <div className="nano-cursess-chips">
              {(savedSession.subjects || []).slice(0, 6).map((s) => (
                <span key={s.id} className="nano-sum-chip">{s.name}</span>
              ))}
              {(savedSession.subjects || []).length > 6 && (
                <span className="nano-sum-chip" style={{ background: 'var(--n-surface-3)', color: 'var(--n-text-2)' }}>
                  +{(savedSession.subjects || []).length - 6} more
                </span>
              )}
            </div>
            <div className="nano-cursess-opts">
              {Object.entries(OPT_LABEL)
                .map(([k, fn]) => fn(savedSession.options ? savedSession.options[k] : null))
                .filter(Boolean)
                .map((t) => <span key={t} className="nano-cursess-opt">{t}</span>)}
              {(savedSession.plan?.focus_areas || []).length > 0 && (
                <span className="nano-cursess-opt focus">
                  Plan: {(savedSession.plan.focus_areas || []).slice(0, 3).map((f) => f.name || f.concept_id.split('.').pop()).join(' · ')}
                  {(savedSession.plan.focus_areas || []).length > 3 ? ' …' : ''}
                </span>
              )}
            </div>
          </div>
          <div className="nano-cursess-actions">
            <button className="nano-cursess-resume" type="button" onClick={onResume}>
              {savedSession.status === 'planned' ? 'Review plan' : 'Resume session'} <IconArrowRight size={15} />
            </button>
            {savedSession.plan && savedSession.status !== 'planned' && (
              <button className="nano-ghost-btn" type="button" onClick={onViewPlan}>View full plan</button>
            )}
            <button className="nano-ghost-btn" type="button" onClick={onNewSession}>＋ New session</button>
          </div>
        </div>
      )}

      <div className="nano-set-body">
        <div className="nano-set-col">
          <div className="nano-card">
            <div className="nano-card-step">Choose your topics</div>
            <div className="nano-card-hint">Select a topic to include all its sub-topics, or drill in and pick specific concepts.</div>
            <div className="nano-search-row">
              <div className="nano-search">
                <IconSearch size={16} style={{ color: 'var(--n-text-3)' }} />
                <input
                  placeholder="Search topics, sub-topics or concepts…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <button className="nano-filter-btn" type="button"><IconFilter size={15} /> Filters</button>
            </div>

            <div className="nano-capture">
              <div className="nano-tree">
                {loadingTree ? (
                  <div className="nano-empty-sel">Loading your Knowledge Hub…</div>
                ) : treeErr ? (
                  <div className="nano-empty-sel">{treeErr}</div>
                ) : tree.length === 0 ? (
                  <div className="nano-empty-sel">Knowledge Hub is empty.<br />Add topics on the Knowledge Hub page.</div>
                ) : (
                  tree.map((n) => renderNode(n, 0))
                )}
              </div>
              <div className="nano-selected">
                <div className="nano-selected-head">
                  <b>Selected ({cover.length})</b>
                  {cover.length > 0 && (
                    <button className="nano-clear" onClick={() => setSelected(new Set())}>Clear All</button>
                  )}
                </div>
                {cover.length === 0 ? (
                  <div className="nano-empty-sel">Nothing selected yet.<br />Pick a topic from the tree on the left.</div>
                ) : (
                  <div className="nano-chips-grid">
                    {cover.map((s) => (
                      <div key={s.id} className="nano-selchip">
                        <span className="nano-selchip-ico" style={{ color: ACCENT }}><IconTarget size={14} /></span>
                        <div style={{ minWidth: 0 }}>
                          <div className="nano-selchip-name">{s.name}</div>
                          <div className="nano-selchip-kind">{KIND_LABEL[s.kind] || s.kind}</div>
                        </div>
                        <button className="nano-selchip-x" onClick={() => toggle(s)}><IconX size={14} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Summary / options / start */}
        <div className="nano-summary">
          <div className="nano-sum-title">Ready to learn</div>
          <div className="nano-sum-sub">Your agent will study your memory and propose a plan you can review before starting.</div>

          <div className="nano-sum-block">
            <div className="nano-sum-label">Session options</div>
            <div className="nano-opts">
              {OPTION_ROWS.map((row) => (
                <div className="nano-opt-row" key={row.key}>
                  <span className="nano-opt-label">{row.label}</span>
                  <div className="nano-seg" role="radiogroup" aria-label={row.label}>
                    {row.choices.map(([val, label]) => (
                      <button
                        key={String(val)}
                        type="button"
                        role="radio"
                        aria-checked={opts[row.key] === val}
                        className={`nano-seg-btn${opts[row.key] === val ? ' on' : ''}`}
                        onClick={() => setOpts((o) => ({ ...o, [row.key]: val }))}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="nano-sum-block">
            <div className="nano-sum-label">Topics ({cover.length})</div>
            {cover.length === 0
              ? <span className="nano-sum-chip" style={{ background: 'var(--n-surface-3)', color: 'var(--n-text-2)' }}>None selected</span>
              : (<>
                {cover.slice(0, 5).map((s) => <span key={s.id} className="nano-sum-chip">{s.name}</span>)}
                {cover.length > 5 && <span className="nano-sum-chip" style={{ background: 'var(--n-surface-3)', color: 'var(--n-text-2)' }}>+{cover.length - 5} more</span>}
              </>)}
          </div>

          <div className="nano-sum-block">
            <div className="nano-sum-label">Modes</div>
            <div style={{ fontSize: 13, color: 'var(--n-text-2)', lineHeight: 1.5 }}>
              All modes are ready — DSA, MCQ, Theory, Flashcards, AI Interviewer, Rapidfire. Switch anytime from the left pane.
            </div>
          </div>

          <div className="nano-sum-meta">
            <span className="lbl"><IconClock size={15} /> Estimated Time</span>
            <span className="val">{estMin}–{estMin + 25} mins</span>
          </div>
          <div className="nano-sum-meta">
            <span className="lbl"><IconTarget size={15} /> Difficulty</span>
            <span className="val">{opts.difficulty ? opts.difficulty : 'Adaptive'}{opts.codingDifficulty ? ` · coding ${opts.codingDifficulty}` : ''}</span>
          </div>

          {startErr && <div className="nano-empty-sel" style={{ marginTop: 10 }}>{startErr}</div>}

          <button className="nano-start-btn" disabled={cover.length === 0 || starting} onClick={start}>
            <IconArrowRight size={17} /> {starting ? 'Planning…' : 'Build my plan'}
          </button>
          <div className="nano-tip" style={{ marginTop: 12 }}>
            <IconSpark size={15} /> The agent shares its plan — and its reasons — before the session starts.
          </div>
        </div>
      </div>
      {starting && <PlannerLoader />}
    </div>
  );
};

export default SessionSettings;
